import { ComponentChildren, RefObject } from 'preact';
import type {
  States,
  NodeRendererContext,
  Bounds,
  DiagramNodeData,
  DiagramLabelPosition,
  Dimensions,
  DiagramLinkData,
  LinkRendererContext,
  DiagramLayoutOutput,
  PromotedLinkId
} from './diagram.types';
import { DiagramNodeElement } from './DiagramNodeElement';
import { layerStyles } from './themes/DiagramStyles.css';
import {
  containerLinksReady,
  getBounds,
  getLinksForNode,
  isContainerReady,
  isLeafNode
} from './utils/diagramUtils';
import { TextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { DiagramLinkLayer } from './DiagramLinkLayer';
import { DiagramPromotedLinkLayer } from './DiagramPromotedLinkLayer';
import { containsKey } from '#utils/UNSAFE_keys';

type Props<K1, K2, D1, D2, L> = {
  nodes: D1[];
  states: States<K1, K2, D2>;
  nodeRenderer: (context: NodeRendererContext<D1>) => ComponentChildren;
  linkRenderer?: (context: LinkRendererContext<K1, D2, L>) => ComponentChildren;
  onNodeSizeChanged: (width: number, height: number, id: K1) => void;
  onLinkSizeChanged: (width: number, height: number, id: K2) => void;
  onPromotedLinkSizeChanged: (width: number, height: number, id: string) => void;
  nodeBounds: Map<K1, Bounds>;
  supportsSelection: boolean;
  minX?: number;
  minY?: number;
  nodeLabelPositions: Map<K1, DiagramLabelPosition>;
  linkLabelPositions: Map<K2, DiagramLabelPosition>;
  labelDimensions: Map<K1 | K2, TextDimensions>;
  links: D2[];
  promotedLinks: PromotedLinkId<K1, D2>[];
  parentId?: K1;
  nodeIdToParentMap: Map<K1, K1>;
  layoutOutput?: L;
  // width height are for svg for links
  width: number;
  height: number;
  zoom: number;
  linkDimensions: Map<K2, Dimensions>;
  promotedLinkDimensions: Map<string, Dimensions>;
  rootRef?: RefObject<HTMLDivElement>;
  combinedNodeBounds: Bounds;
  nodeBoundsRef: RefObject<Map<K1, Bounds>>;
  normalizeLinks?: boolean;
};

export function DiagramNodeLayer<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
>({
  nodes,
  states,
  nodeRenderer,
  linkRenderer,
  nodeBounds,
  width,
  height,
  onNodeSizeChanged,
  onLinkSizeChanged,
  onPromotedLinkSizeChanged,
  supportsSelection,
  nodeLabelPositions,
  linkLabelPositions,
  labelDimensions,
  minX = 0,
  minY = 0,
  parentId,
  links,
  promotedLinks,
  layoutOutput,
  zoom,
  nodeIdToParentMap,
  linkDimensions,
  promotedLinkDimensions,
  rootRef,
  combinedNodeBounds,
  nodeBoundsRef,
  normalizeLinks = false
}: Props<K1, K2, D1, D2, L>) {
  const { frontLinks, frontPromotedLinks, backLinks, backPromotedLinks } = getLinksForNode<
    K1,
    K2,
    D2
  >(parentId, links, promotedLinks, nodeIdToParentMap);
  return (
    <div class={layerStyles} data-oj-private-node-container>
      {layoutOutput &&
        linkRenderer &&
        backLinks.length > 0 &&
        containerLinksReady<K1, K2, D2, L>(backLinks, layoutOutput) && (
          <DiagramLinkLayer
            links={backLinks}
            states={states}
            linkRenderer={linkRenderer}
            width={width / zoom}
            height={height}
            supportsSelection={supportsSelection}
            labelDimensions={labelDimensions}
            layoutOutput={layoutOutput}
            onLinkSizeChanged={onLinkSizeChanged}
            linkLabelPositions={linkLabelPositions}
            combinedNodeBounds={combinedNodeBounds}
            minY={normalizeLinks ? minY : 0}
            minX={normalizeLinks ? minX : 0}
          />
        )}
      {layoutOutput &&
        linkRenderer &&
        backPromotedLinks.length > 0 &&
        containerLinksReady<K1, K2, D2, L>(backPromotedLinks, layoutOutput) && (
          <DiagramPromotedLinkLayer
            links={backPromotedLinks as PromotedLinkId<K1, D2>[]}
            states={states}
            linkRenderer={linkRenderer}
            width={width / zoom}
            height={height}
            supportsSelection={supportsSelection}
            layoutOutput={layoutOutput}
            onLinkSizeChanged={onPromotedLinkSizeChanged}
            combinedNodeBounds={combinedNodeBounds}
            minY={normalizeLinks ? minY : 0}
            minX={normalizeLinks ? minX : 0}
          />
        )}

      {nodes.map((node) => {
        // this is for initial render before the layout function so render all nodes at 0,0
        const position =
          nodeBounds && nodeBounds.get(node.id)
            ? { x: nodeBounds.get(node.id)!.x - minX, y: nodeBounds.get(node.id)!.y - minY }
            : undefined;
        const state = {
          isSelected: states.selectedNodeIds.includes(node.id),
          isFocused: states.focusedId === node.id,
          isHovered: states.hoveredId === node.id,
          zoom: states.zoom,
          isExpanded: states.expanded && containsKey(states.expanded, node.id)
        };
        const { backLinks, backPromotedLinks } = getLinksForNode<K1, K2, D2>(
          node.id,
          links,
          promotedLinks,
          nodeIdToParentMap
        );

        // Leaf node or node is collapsed
        if (isLeafNode(node) || !containsKey(states.expanded, node.id)) {
          return (
            <DiagramNodeElement
              id={node.id}
              position={position}
              state={state}
              isDimmed={
                (states.highlightedNodeIds.length !== 0 ||
                  states.highlightedLinkIds.length !== 0 ||
                  states.highlightedPromotedLinkIds.length !== 0) &&
                !states.highlightedNodeIds.includes(node.id)
              }
              activeId={states.activeId}
              nodeRenderer={nodeRenderer}
              onNodeSizeChanged={onNodeSizeChanged}
              accessibleLabel={node.accessibleLabel}
              data={node}
              supportsSelection={supportsSelection}
              labelDimensions={labelDimensions}
              labelPosition={nodeLabelPositions?.get(node.id)}
            />
          );
          // Expanded container where children are ready
        } else if (
          nodeBounds &&
          isContainerReady<K1, K2, D1, D2>(
            node,
            nodeBounds,
            backLinks,
            backPromotedLinks,
            linkDimensions,
            promotedLinkDimensions
          )
        ) {
          const childContent = getContent<K1, K2, D1, D2, L>(
            node,
            states,
            nodeRenderer,
            onNodeSizeChanged,
            onLinkSizeChanged,
            onPromotedLinkSizeChanged,
            supportsSelection,
            links,
            promotedLinks,
            nodeIdToParentMap,
            width,
            height,
            zoom,
            node.id,
            nodeBounds,
            linkDimensions,
            promotedLinkDimensions,
            combinedNodeBounds,
            nodeBoundsRef,
            nodeLabelPositions,
            linkLabelPositions,
            labelDimensions,
            linkRenderer,
            layoutOutput,
            rootRef
          );
          return (
            <DiagramNodeElement
              id={node.id}
              position={position}
              state={state}
              isDimmed={
                (states.highlightedNodeIds.length !== 0 ||
                  states.highlightedLinkIds.length !== 0 ||
                  states.highlightedPromotedLinkIds.length !== 0) &&
                !states.highlightedNodeIds.includes(node.id)
              }
              activeId={states.activeId}
              nodeRenderer={nodeRenderer}
              onNodeSizeChanged={onNodeSizeChanged}
              accessibleLabel={node.accessibleLabel}
              data={node}
              supportsSelection={supportsSelection}
              content={childContent}
              labelDimensions={labelDimensions}
              labelPosition={nodeLabelPositions?.get(node.id)}
            />
          );
        } else {
          const childContent = getContent<K1, K2, D1, D2, L>(
            node,
            states,
            nodeRenderer,
            onNodeSizeChanged,
            onLinkSizeChanged,
            onPromotedLinkSizeChanged,
            supportsSelection,
            links,
            promotedLinks,
            nodeIdToParentMap,
            width,
            height,
            zoom,
            node.id,
            nodeBounds,
            linkDimensions,
            promotedLinkDimensions,
            combinedNodeBounds,
            nodeBoundsRef,
            nodeLabelPositions,
            linkLabelPositions,
            labelDimensions,
            linkRenderer,
            layoutOutput,
            rootRef
          );
          return <div>{childContent.content}</div>;
        }
      })}
      {layoutOutput &&
        linkRenderer &&
        frontLinks.length > 0 &&
        containerLinksReady<K1, K2, D2, L>(frontLinks, layoutOutput) && (
          <DiagramLinkLayer
            links={frontLinks}
            states={states}
            linkRenderer={linkRenderer}
            width={width / zoom}
            height={height}
            supportsSelection={supportsSelection}
            labelDimensions={labelDimensions}
            layoutOutput={layoutOutput}
            onLinkSizeChanged={onLinkSizeChanged}
            linkLabelPositions={linkLabelPositions}
            combinedNodeBounds={combinedNodeBounds}
            minY={normalizeLinks ? minY : 0}
            minX={normalizeLinks ? minX : 0}
          />
        )}
      {layoutOutput &&
        linkRenderer &&
        frontPromotedLinks.length > 0 &&
        containerLinksReady<K1, K2, D2, L>(frontPromotedLinks, layoutOutput) && (
          <DiagramPromotedLinkLayer
            links={frontPromotedLinks as PromotedLinkId<K1, D2>[]}
            states={states}
            linkRenderer={linkRenderer}
            width={width / zoom}
            height={height}
            supportsSelection={supportsSelection}
            layoutOutput={layoutOutput}
            onLinkSizeChanged={onPromotedLinkSizeChanged}
            combinedNodeBounds={combinedNodeBounds}
            minY={normalizeLinks ? minY : 0}
            minX={normalizeLinks ? minX : 0}
          />
        )}
    </div>
  );
}

function getContent<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
>(
  nodeData: D1,
  states: States<K1, K2, D2>,
  nodeRenderer: (context: NodeRendererContext<D1>) => ComponentChildren,
  onNodeSizeChanged: (width: number, height: number, id: K1) => void,
  onLinkSizeChanged: (width: number, height: number, id: K2) => void,
  onPromotedLinkSizeChanged: (width: number, height: number, id: string) => void,
  supportsSelection: boolean,
  links: D2[],
  promotedLinks: PromotedLinkId<K1, D2>[],
  nodeIdToParentMap: Map<K1, K1>,
  width: number,
  height: number,
  zoom: number,
  parentId: K1,
  nodeBounds: Map<K1, Bounds>,
  linkDimensions: Map<K2, Dimensions>,
  promotedLinkDimensions: Map<string, Dimensions>,
  combinedNodeBounds: Bounds,
  nodeBoundsRef: RefObject<Map<K1, Bounds>>,
  nodeLabelPositions: Map<K1, DiagramLabelPosition>,
  linkLabelPositions: Map<K2, DiagramLabelPosition>,
  labelDimensions: Map<K1 | K2, TextDimensions>,
  linkRenderer?: (context: LinkRendererContext<K1, D2, L>) => ComponentChildren,
  layoutOutput?: L,
  rootRef?: RefObject<HTMLDivElement>
) {
  const nodes = nodeData.nodes!;
  const { backLinks, backPromotedLinks } = getLinksForNode<K1, K2, D2>(
    parentId,
    links,
    promotedLinks,
    nodeIdToParentMap
  );
  const { minX, minY, contentWidth, contentHeight, bounds } = getBounds(
    nodes,
    backLinks,
    backPromotedLinks,
    nodeBounds,
    linkDimensions,
    promotedLinkDimensions,
    zoom,
    rootRef
  );
  const nodeFullBounds = nodeBoundsRef.current;
  nodeFullBounds?.set(nodeData.id, bounds);
  nodeBoundsRef.current = nodeFullBounds;

  const content = (
    <div data-oj-private-child={parentId} style={{ position: 'relative' }}>
      <DiagramNodeLayer
        nodes={nodes}
        nodeBounds={nodeBounds}
        states={states}
        minX={nodeBounds ? minX : undefined}
        minY={nodeBounds ? minY : undefined}
        nodeRenderer={nodeRenderer}
        onNodeSizeChanged={onNodeSizeChanged}
        onLinkSizeChanged={onLinkSizeChanged}
        onPromotedLinkSizeChanged={onPromotedLinkSizeChanged}
        supportsSelection={supportsSelection}
        nodeLabelPositions={nodeLabelPositions}
        linkLabelPositions={linkLabelPositions}
        labelDimensions={labelDimensions}
        links={links}
        promotedLinks={promotedLinks}
        nodeIdToParentMap={nodeIdToParentMap}
        linkRenderer={linkRenderer}
        layoutOutput={layoutOutput}
        width={width}
        height={height}
        zoom={zoom}
        parentId={parentId}
        linkDimensions={linkDimensions}
        promotedLinkDimensions={promotedLinkDimensions}
        rootRef={rootRef}
        combinedNodeBounds={combinedNodeBounds}
        nodeBoundsRef={nodeBoundsRef}
      />
    </div>
  );
  return {
    content,
    width: contentWidth ? contentWidth : 0,
    height: contentHeight ? contentHeight - minY! : 0,
    bounds
  };
}
